/** Domain types — mirror what the Express API actually returns. */
import type { Key } from '../i18n';

export type Role = 'user' | 'manager' | 'admin' | 'supervisor';
/** `deleted` is an anonymised account — the row survives, the person doesn't. */
export type UserStatus = 'active' | 'pending' | 'suspended' | 'deleted';

export interface User {
  id: number;
  name: string;
  phone: string;
  email: string | null;
  address: string | null;
  role: Role;
  status: UserStatus;
  /** Supervisors only: whether the manager granted them venue editing. */
  can_edit_halls?: boolean;
}

/** Payload for PUT /auth/me. The mobile number is not editable. */
export interface ProfileInput {
  name: string;
  email: string;
  address: string;
  /** Both required together to change the password; omit to leave it alone. */
  currentPassword?: string;
  newPassword?: string;
}

export type Catering = 'none' | 'inhouse' | 'outside' | 'both';
export type Cuisine = 'veg' | 'nonveg' | 'both';

/** Legal-verification lifecycle for a venue. */
export type VerificationStatus = 'pending' | 'verified' | 'rejected';

export type HallDocType = 'occupancy_certificate' | 'amusement_license';

/** A legal document a manager uploads to prove a venue is permitted for public use. */
export interface HallDocument {
  doc_type: HallDocType;
  file_url: string;
  file_name?: string | null;
  issuing_authority?: string | null;
  reference_no?: string | null;
}

/** The two documents every venue must supply before an admin can verify it. */
/** The two licences every venue must have on file, named by translation key —
 *  the `type` is what the server stores, the label is only what a manager reads. */
export const REQUIRED_HALL_DOCS: { type: HallDocType; label: Key; hint: Key }[] = [
  {
    type: 'occupancy_certificate',
    label: 'admDocOccupancy',
    hint: 'hallDocOccupancyHint',
  },
  {
    type: 'amusement_license',
    label: 'admDocLicense',
    hint: 'hallDocLicenseHint',
  },
];

/** The venue types, from the single list in lib/venueTypes.ts (kept under this name for existing imports). */
export { VENUE_TYPES as HALL_CATEGORIES } from '../lib/venueTypes';

/** A hall as returned by hallView() on the server (JSON columns parsed, derived fields added). */
/** One bookable session of a venue's day. `from`/`to` are whole hours. */
/** A refund on a cancelled booking: none owed, owed by the venue, or returned. */
export type RefundStatus = 'none' | 'pending' | 'refunded';

/**
 * What each booking mode costs at a venue (server lib.js bookingModes) — null
 * for a mode it does not offer. The booking page offers exactly these.
 */
export interface BookingModes {
  hour: number | null;
  /** Single sessions can be priced (a half-day or hourly price is set). */
  session: boolean;
  halfday: number | null;
  /** The day rate: the full-day tile and several-days hire. */
  day: number | null;
  /** The two half-days, in whole hours. */
  halfday_windows: { key: 'first' | 'second'; from: number; to: number }[];
  /** 1 when weekends cost the same. */
  weekend_multiplier: number;
}

/** One line of a quote: count x rate = amount. */
export interface QuoteLine {
  kind: 'hour' | 'halfday' | 'day';
  count: number;
  rate: number;
  amount: number;
  weekend: boolean;
}

export interface Quote {
  price: number;
  lines?: QuoteLine[];
  weekend_multiplier?: number;
}

export interface HallSession {
  key: 'morning' | 'afternoon' | 'evening';
  label: string;
  from: number;
  to: number;
}

/** A building that groups several of a manager's halls. */
export interface Property {
  id: number;
  name: string;
  hall_count?: number;
}

export interface Hall {
  id: number;
  /** The building this hall is part of, if any. */
  property_id?: number | null;
  property_name?: string | null;
  /** Set only when the venue list is filtered by date: free all day, or partly booked. */
  date_availability?: 'free' | 'partial';
  /** With 'partial': how many of the day's sessions are still open. */
  free_sessions?: number;
  manager_id: number;
  name: string;
  category: string;
  description: string | null;
  location: string | null;
  locality: string | null;
  city: string | null;
  pincode: string | null;
  contact_phone: string | null;
  established_year: number | null;
  capacity: number;
  floating_capacity: number;
  /** AC rooms. */
  rooms: number;
  /** Rooms without AC (0 = none) and how many people they sleep in all. */
  non_ac_rooms: number;
  non_ac_room_capacity: number;
  /** The dining / food area: seats in all, and how many of them are AC. */
  dining_capacity: number;
  dining_ac_capacity: number;
  /** A Google Maps link to the venue, when the manager gave one. */
  maps_url: string | null;
  ac: number;
  lift: number;
  car_parking: number;
  bike_parking: number;
  catering: Catering;
  cuisine: Cuisine;
  amenities: string[];
  photos: string[];
  /** Optional 360° / panoramic image URL guests can drag to look around. */
  pano_360: string | null;
  seating_layout: string | null;
  hourly_price: number;
  halfday_price: number;
  fullday_price: number;
  peak_multiplier: number;
  buffer_minutes: number;
  allow_hourly: boolean;
  active: boolean;
  created_at: number;
  // The venue's own session hours, whole hours 0-24.
  morning_from: number;
  morning_to: number;
  afternoon_from: number;
  afternoon_to: number;
  evening_from: number;
  evening_to: number;
  /** The same three, ready for the picker. Derived server-side. */
  sessions: HallSession[];
  /** Offered booking modes and their prices. Absent from older servers. */
  booking_modes?: BookingModes;
  /** When the gallery was last changed (falls back to created_at for older venues). */
  photos_updated_at: number | null;
  // legal verification
  verification_status: VerificationStatus;
  verification_notes: string | null;
  verified_at: number | null;
  /** Uploaded legal documents — present on the manager's own halls only. */
  documents?: HallDocument[];
  // derived server-side
  address: string;
  map_query: string;
  map_embed: string;
  has_ac: boolean;
  has_lift: boolean;
  has_parking: boolean;
  rating: number;
  review_count: number;
}

export interface Review {
  id: number;
  hall_id: number;
  user_id: number;
  user_name: string;
  rating: number;
  comment: string | null;
  created_at: number;
}

/**
 * 'pending' is a request the guest has made and the venue has not answered yet;
 * only 'confirmed' occupies the calendar. 'expired' survives for rows retired by
 * the old checkout-hold sweep — nothing produces it any more.
 */
export type BookingStatus = 'pending' | 'confirmed' | 'cancelled' | 'expired' | 'completed';
export type PaymentStatus = 'unpaid' | 'partial' | 'paid' | 'refunded';

/** The columns every view of a booking carries. */
export interface Booking {
  id: number;
  hall_id: number;
  start_time: number;
  end_time: number;
  price: number;
  status: BookingStatus;
  created_at: number;
  /** What the guest said the event is (content/occasions.ts EVENT_TYPES); null for older bookings. */
  event_type?: string | null;
  /** Expected head count, when the guest gave one. */
  guests?: number | null;
}

/**
 * /bookings/mine — the guest's own bookings.
 *
 * Guests do not pay through the app — they pay at the venue — but they see what
 * the venue has recorded as received. `contact_phone` is
 * the venue's own number, which is how a guest chases a request the manager has
 * not answered yet.
 */
export interface MyBooking extends Booking {
  amount_paid: number;
  payment_status: PaymentStatus;
  /** What a cancellation owes back, and whether the venue has returned it. */
  refund_amount?: number;
  refund_status?: RefundStatus;
  refunded_at?: number | null;
  hall_name: string;
  location: string | null;
  city: string | null;
  contact_phone: string | null;
  /** First entry of the hall's photos array, or null if it has none. */
  hall_photo: string | null;
}

/** /manager/bookings joins the hall and the guest — the venue's view, which
 *  does track what was collected at the door. */
export interface ManagerBooking extends Booking {
  user_id: number;
  amount_paid: number;
  payment_status: PaymentStatus;
  refund_amount?: number;
  refund_status?: RefundStatus;
  refunded_at?: number | null;
  notes: string | null;
  hall_name: string;
  user_name: string;
  user_email: string | null;
  user_phone: string | null;
  /** The manager who took it at the venue; null for a guest's own request. */
  booked_by: number | null;
  /** Payment record (the original charge), null until something is paid. */
  receipt_no: string | null;
  provider: string | null;
  /** The gateway's own reference for the payment (Razorpay link/txn id). */
  provider_ref: string | null;
  method: string | null;
  paid_amount: number | null;
  paid_at: number | null;
}

/** What POST /bookings/request answers with: the pending booking, plus the
 *  venue's number for the call/WhatsApp buttons the guest is shown next. */
export interface BookingRequest {
  booking: {
    id: number;
    price: number;
    status: 'pending';
    start_time: number;
    end_time: number;
  };
  venue: {
    id: number;
    name: string;
    contact_phone: string | null;
  };
  /** True when this repeats a request the venue already has. */
  duplicate: boolean;
}

export interface BusyWindow {
  /** Lets a reschedule ignore the slot the booking already holds. */
  id: number;
  start_time: number;
  end_time: number;
  status: BookingStatus;
}

export interface Block {
  id: number;
  hall_id: number;
  start_time: number;
  end_time: number;
  reason: string | null;
}

/** working = nothing wrong; the rest describe what is wrong with `affected_qty`. */
export type AssetStatus = 'working' | 'maintenance' | 'damaged' | 'retired';

/** A thing a venue owns. `affected_qty` is how many of `quantity` are in `status`. */
export interface Asset {
  id: number;
  hall_id: number;
  hall_name: string;
  name: string;
  quantity: number;
  status: AssetStatus;
  affected_qty: number;
  note: string | null;
  category: AssetCategory | null;
  /** Where in the venue it is kept — "Stage", "Store room". */
  location: string | null;
  /** Make / model. */
  brand: string | null;
  /** Serial number, or the venue's own asset tag. */
  serial_no: string | null;
  /** YYYY-MM-DD */
  purchased_on: string | null;
  /** YYYY-MM-DD */
  warranty_until: string | null;
  last_checked_at: number;
  created_at: number;
  updated_at: number | null;
}

export const ASSET_CATEGORIES = [
  'furniture', 'audio_visual', 'lighting', 'electrical', 'climate',
  'kitchen', 'decor', 'safety', 'cleaning', 'other',
] as const;
/** One of the admin's asset categories (lib/master.ts); ASSET_CATEGORIES are the built-in ones. */
export type AssetCategory = string;

export interface AssetInput {
  name: string;
  quantity: number;
  status: AssetStatus;
  affectedQty: number;
  note: string;
  category: AssetCategory | '';
  location: string;
  brand: string;
  serialNo: string;
  purchasedOn: string;
  warrantyUntil: string;
}

export interface Availability {
  date: string;
  busy: BusyWindow[];
  /**
   * Windows other people have asked for and the venue has not answered yet.
   *
   * Deliberately separate from `busy`: a request reserves nothing, and the slot
   * is still for sale until a manager confirms one of them. It is shown so the
   * grid can stop calling a contested Saturday simply "free" — the guest is
   * told they would be one of several asking, rather than finding out when the
   * request comes back declined.
   */
  requested: Pick<BusyWindow, 'start_time' | 'end_time'>[];
  blocks: Pick<Block, 'start_time' | 'end_time' | 'reason'>[];
}

/** The life of a lead, in order. */
export const ENQUIRY_STATUSES = ['new', 'contacted', 'follow_up', 'interested', 'booked', 'closed'] as const;
export type EnquiryStatus = (typeof ENQUIRY_STATUSES)[number];

export interface Enquiry {
  id: number;
  hall_id: number;
  hall_name: string;
  name: string;
  phone: string;
  email: string | null;
  event_date: number | null;
  guests: number | null;
  message: string | null;
  status: EnquiryStatus;
  created_at: number;
  /** The venue's running log of calls and messages with the guest. */
  notes?: string | null;
  /** The day to get back to the guest, YYYY-MM-DD. */
  follow_up_on?: string | null;
  /** Last time staff touched the lead. */
  updated_at?: number | null;
}

export interface Notification {
  id: number;
  title: string;
  body: string | null;
  channel: string;
  read: number;
  created_at: number;
}

export interface ManagerReports {
  /** All-time net revenue. */
  revenue: number;
  /** Net revenue over the same 12 months as perMonth — what the tile shows. */
  revenue12?: number;
  counts: { status: BookingStatus; c: number }[];
  perHall: { name: string; bookings: number; revenue: number }[];
  popularHours: { hour: string; c: number }[];
  /** Revenue + bookings for each of the last 12 calendar months (oldest first). */
  perMonth: { ym: string; revenue: number; bookings: number }[];
}

export interface AdminOverview {
  users: number;
  managers: number;
  pendingManagers: number;
  suspendedUsers: number;
  halls: number;
  activeHalls: number;
  bookings: number;
  pendingBookings: number;
  gmv: number;
  refunds: number;
  newMessages: number;
  newEnquiries: number;
  topHalls: { name: string; manager_name: string; bookings: number; revenue: number }[];
}

/** Admin manager row — includes contact details and venue counts. */
export interface ManagerAccount {
  id: number;
  name: string;
  phone: string;
  email: string | null;
  address: string | null;
  status: UserStatus;
  created_at: number;
  hall_count: number;
  active_hall_count: number;
  /** Set only while suspended; shown to them at the login screen. */
  suspension_reason: string | null;
  suspended_at: number | null;
  /** Government ID supplied at registration, for the admin to review.
   *  Null for managers who registered before this was required. */
  id_proof_type: string | null;
  id_proof_number: string | null;
  id_proof_url: string | null;
  id_proof_name: string | null;
  id_proof_uploaded_at: number | null;
}

/** /admin/managers/:id/documents — everything one manager has uploaded. */
export interface ManagerDocuments {
  manager: Pick<
    ManagerAccount,
    'id' | 'name' | 'id_proof_type' | 'id_proof_number' | 'id_proof_url' | 'id_proof_name' | 'id_proof_uploaded_at'
  >;
  halls: { id: number; name: string; verification_status: VerificationStatus; documents: HallDocument[] }[];
}

/** A supervisor account a manager created, as /manager/supervisors lists it. */
export interface Supervisor {
  id: number;
  name: string;
  phone: string;
  email: string | null;
  status: UserStatus;
  created_at: number;
  /** The halls this supervisor looks after. Empty means they see nothing. */
  hall_ids: number[];
  /** Whether they may edit those venues, or only run them. */
  can_edit_halls: boolean;
}

/** Any account, as the admin console lists it. */
export interface AdminUser {
  id: number;
  name: string;
  phone: string;
  email: string | null;
  address: string | null;
  role: Role;
  status: UserStatus;
  created_at: number;
  booking_count: number;
  /** Net of refunds — a fully refunded booker nets back to 0. */
  spend: number;
  /** Set only while suspended; shown to them at the login screen. */
  suspension_reason: string | null;
  suspended_at: number | null;
}

/** A hall as the admin console lists it — every venue, live or not. */
export interface AdminHall {
  id: number;
  name: string;
  category: string;
  city: string | null;
  locality: string | null;
  capacity: number;
  hourly_price: number;
  active: boolean;
  verification_status: VerificationStatus;
  verification_notes: string | null;
  verified_at: number | null;
  document_count: number;
  created_at: number;
  manager_id: number;
  manager_name: string;
  manager_status: UserStatus;
  booking_count: number;
  revenue: number;
  rating: number;
  review_count: number;
}

/** Full venue detail for the admin verification panel (GET /admin/halls/:id). */
export interface AdminHallDetail {
  hall: Hall;
  documents: HallDocument[];
  manager: { id: number; name: string; phone: string; email: string | null; status: UserStatus };
  reviews: Review[];
}

/** /admin/bookings joins the hall, the guest and the owning manager. */
export interface AdminBooking extends Booking {
  user_id: number;
  amount_paid: number;
  payment_status: PaymentStatus;
  hall_name: string;
  city: string | null;
  user_name: string;
  user_phone: string;
  manager_name: string;
}

/** Filters accepted by GET /admin/bookings. Dates are YYYY-MM-DD. */
export interface AdminBookingFilters {
  status?: string;
  q?: string;
  from?: string;
  to?: string;
}

export type MessageStatus = 'new' | 'handled';

/** A "contact us" submission from the public form. */
export interface ContactMessage {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
  subject: string | null;
  message: string;
  status: MessageStatus;
  created_at: number;
  /** Set only on suspension appeals — the account being appealed for. NULL for
   *  ordinary contact-form submissions, which is what makes them actionable. */
  user_id: number | null;
  doc_url: string | null;
  doc_name: string | null;
  account_role: Role | null;
  account_status: UserStatus | null;
  account_suspension_reason: string | null;
}

/** An enquiry, with the venue's owning manager attached. */
export interface AdminEnquiry extends Enquiry {
  manager_name: string;
}

export interface AuditEntry {
  id: number;
  actor_id: number | null;
  actor_name: string | null;
  actor_role: Role | null;
  action: string;
  entity: string | null;
  entity_id: number | null;
  details: string | null;
  created_at: number;
}

/** Payload accepted by POST/PUT /manager/halls. */
export interface HallInput {
  property_id?: number | null;
  name: string;
  category: string;
  description: string;
  location: string;
  locality: string;
  city: string;
  pincode: string;
  contact_phone: string;
  established_year: number | null;
  capacity: number;
  floating_capacity: number;
  rooms: number;
  non_ac_rooms: number;
  non_ac_room_capacity: number;
  dining_capacity: number;
  dining_ac_capacity: number;
  maps_url: string;
  ac: boolean;
  lift: boolean;
  car_parking: number;
  bike_parking: number;
  catering: Catering;
  cuisine: Cuisine;
  amenities: string[];
  photos: string[];
  pano_360: string;
  seating_layout: string;
  hourly_price: number;
  halfday_price: number;
  fullday_price: number;
  peak_multiplier: number;
  buffer_minutes: number;
  allow_hourly: boolean;
  active: boolean;
  morning_from: number;
  morning_to: number;
  afternoon_from: number;
  afternoon_to: number;
  evening_from: number;
  evening_to: number;
  /** The two required legal documents (occupancy_certificate + amusement_license). */
  documents: HallDocument[];
}

/** Browse filters — mirrors the query string GET /halls accepts. */
export interface HallFilters {
  q?: string;
  location?: string;
  category?: string;
  minCap?: string;
  maxPrice?: string;
  ac?: string;
  /** "1" = only venues with lift access. */
  lift?: string;
  parking?: string;
  sort?: string;
  /** "YYYY-MM-DD" — show only venues free for the whole of this day. */
  date?: string;
}

/** How money taken directly by the venue came in. */
export type PaymentMethod = 'cash' | 'upi' | 'bank' | 'card' | 'other';

/** A venue's equipment as a guest sees it on the venue page — condition only. */
export interface HallEquipment {
  name: string;
  category: AssetCategory | null;
  quantity: number;
  status: 'working' | 'maintenance' | 'damaged';
  /** How many are out of action right now. */
  unavailable: number;
  /** How many can be used. */
  available: number;
}
